import { baseUrl } from "../config/env";
import {
  finalCta,
  firstMonth,
  heroCopy,
  offers,
  processSteps,
  sampleReport,
  whyThisWorks,
  type Offer
} from "../config/offers";
import { accentRailMarkup, checkList, siteFooter, topNav } from "./layout";

function sampleReportCard(assetBase: string): string {
  return `
    <aside class="sample-report" aria-label="Sample monthly Foxmark report">
      ${accentRailMarkup("sample-report-rail")}
      <div class="report-head">
        <div>
          <img class="client-logo" src="${assetBase}assets/hillside-dental.png" alt="${sampleReport.business}" width="496" height="406" />
        </div>
        <div class="trust-leak-stack">
          <p class="report-date">${sampleReport.date}</p>
          <section class="leak-row" aria-label="Top trust leak found">
            <div class="warning-mark" aria-hidden="true">
              <svg viewBox="0 0 64 64" fill="none">
                <path d="M32 10 56 52H8L32 10Z" stroke="currentColor" stroke-width="5" stroke-linejoin="round" />
                <path d="M32 25v14" stroke="currentColor" stroke-width="5" stroke-linecap="square" />
                <path d="M32 47h.01" stroke="currentColor" stroke-width="7" stroke-linecap="square" />
              </svg>
            </div>
            <div>
              <p class="section-label leak-title">Top Trust Leak Found</p>
              <p class="leak-copy">Customers praise “gentle with kids,” but that proof is missing near the booking CTA.</p>
              <p class="fix-row">
                <span class="status-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" />
                    <path d="m8 12 3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="square" />
                  </svg>
                </span>
                <span><strong>Fix Shipped:</strong> Added review highlights near the booking CTA.</span>
              </p>
            </div>
          </section>
        </div>
      </div>

      <section class="metric-grid" aria-label="Monthly report metrics">
        <article>
          <strong>4.6 <em>↑ 0.2</em></strong>
          <span>Reputation score</span>
          <p>Trending up</p>
        </article>
        <article>
          <strong>12/12</strong>
          <span>Reviews answered</span>
          <p>100% response rate</p>
        </article>
        <article>
          <strong>3</strong>
          <span>Trust leaks fixed</span>
          <p>This month</p>
        </article>
      </section>

      <section class="report-detail-grid">
        <div class="customer-language">
          <p class="block-label">Customers keep mentioning</p>
          <ul>
            <li>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" stroke="currentColor" stroke-width="2" />
              </svg>
              <span>“${sampleReport.language[0]}”</span>
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" />
                <path d="M12 7v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="square" />
              </svg>
              <span>“${sampleReport.language[1]}”</span>
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2l-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
              </svg>
              <span>“${sampleReport.language[2]}”</span>
            </li>
          </ul>
        </div>
        <div class="next-fix">
          <div class="idea-icon" aria-hidden="true">
            <svg viewBox="0 0 64 64" fill="none">
              <path d="M22 30a10 10 0 1 1 20 0c0 5-4 7-6 10H28c-2-3-6-5-6-10Z" stroke="currentColor" stroke-width="3" />
              <path d="M28 47h8M29 54h6M32 8v6M48 16l-4 4M56 32h-6M16 16l4 4M8 32h6" stroke="currentColor" stroke-width="3" stroke-linecap="square" />
            </svg>
          </div>
          <div>
            <p class="block-label">Next recommended fix</p>
            <p>Add family-care proof above the fold and near the appointment button.</p>
          </div>
        </div>
      </section>

      <div class="report-bottom-bar">
        <p>${sampleReport.caption}</p>
        <p>Full report includes all findings, evidence, and fix plan.</p>
      </div>
    </aside>
  `;
}

function offerCard(offer: Offer): string {
  return `
    <article class="offer-card decision-card">
      <div class="offer-body">
        <div class="offer-head">
          <p class="eyebrow">${offer.label}</p>
          <p class="price">${offer.price}</p>
        </div>
        <h3>${offer.cardName}</h3>
        <p class="offer-outcome">${offer.outcome}</p>
        <ul class="benefit-list">${checkList(offer.benefits)}</ul>
        <p class="ideal-for"><span>Ideal for</span> ${offer.idealFor}</p>
        <div class="offer-actions">
          <a class="button button-primary" href="${offer.paymentUrl}">${offer.cta}</a>
          <a class="offer-details" href="${offer.pagePath}">View everything included →</a>
        </div>
      </div>
    </article>
  `;
}

export function renderFoxmarkLandingPage(): string {
  const assetBase = baseUrl();

  return `
    <main>
      ${topNav(assetBase)}

      <section id="top" class="hero" data-screen-label="Hero">
        <div class="hero-shader" data-hero-shader data-image-src="${assetBase}assets/foxmark-logo-mark.svg" aria-hidden="true">
          <img class="hero-shader-fallback" src="${assetBase}assets/foxmark-logo-mark.svg" alt="" />
        </div>
        <div class="hero-grid">
          <div class="hero-copy">
            <div class="hero-kicker">
              ${accentRailMarkup()}
              <span class="hero-eyebrow">${heroCopy.tagline}</span>
            </div>
            <h1>${heroCopy.headline}</h1>
            <p class="hero-subhead">${heroCopy.subhead}</p>
            <div class="actions">
              <a class="button button-primary" href="#services">${heroCopy.primaryCta}</a>
              <a class="button button-outline" href="/google-review-management">See Review Management</a>
            </div>
            <p class="proof-line">${heroCopy.proofLine}</p>
          </div>
          ${sampleReportCard(assetBase)}
        </div>
      </section>

      <section id="services" class="section" data-screen-label="Services">
        <div class="section-heading">
          ${accentRailMarkup()}
          <h2>Two monthly packs. Pick the leak you want closed first.</h2>
          <p>One flat price, one concrete deliverable pack per month. No dashboards to learn, no meetings to sit through.</p>
        </div>
        <div class="offer-grid">${offers.map(offerCard).join("")}</div>
      </section>

      <section id="proof" class="section section-soft" data-screen-label="Proof">
        <div class="proof-layout">
          <h2>${whyThisWorks.headline}</h2>
          <div class="proof-list">${whyThisWorks.points
      .map((option, index) => `<article><span>0${index + 1}</span><p>${option}</p></article>`)
      .join("")}</div>
        </div>
      </section>

      <section id="process" class="section" data-screen-label="Process">
        <div class="section-heading">
          <h2>How every month runs</h2>
        </div>
        <div class="process-grid">${processSteps
      .map((step) => `<article class="spec-cell"><span>${step.number}</span><h3>${step.title}</h3><p>${step.body}</p></article>`)
      .join("")}</div>
      </section>

      <section id="what-you-get" class="section section-soft" data-screen-label="What You Get">
        <div class="section-heading section-heading-inline">
          <div class="inline-label">
            ${accentRailMarkup()}
            <span>Monthly pack checklist</span>
          </div>
          <h2>${firstMonth.headline}</h2>
          <p>${firstMonth.body}</p>
        </div>
        <div class="asset-grid">${firstMonth.deliverables
      .map((item) => `<article><span>REQ</span><p>${item}</p></article>`)
      .join("")}</div>
      </section>

      <section id="start" class="final-cta" data-screen-label="Final CTA">
        ${accentRailMarkup()}
        <h2>${finalCta.headline}</h2>
        <p>${finalCta.body}</p>
        <div class="actions actions-centered">
          <a class="button button-primary" href="${offers[0].pagePath}">${offers[0].cta}</a>
          <a class="button button-outline" href="${offers[1].pagePath}">${offers[1].cta}</a>
        </div>
        <p class="proof-line">${finalCta.microcopy}</p>
      </section>
    </main>

    ${siteFooter()}
  `;
}
